/**
 * The pure half of the QuickBooks IIF customer import: QB CUST rows → the rows
 * POSTed to /customers/bulk. No network, no app state — shared by the importer
 * (services/qb-lists-import.ts), the pre-import check (utils/qb-preflight.ts) and the
 * end-to-end import spec, so all three see exactly the same numbering and rows.
 */

import { buildIifCustomerRow, type IifCustomerRow, parseCustomerName } from '@/utils/iif-customer-row';
import { parseNameFlags } from '@/utils/parse-name-flags';

/** A numbering decision worth telling the person importing about. */
export interface NumberingNote {
	/** The unit's base number, e.g. "5977AS". */
	base: string;
	kind: 'office-numbering' | 'duplicate-live';
	message: string;
}

export interface Disambiguation {
	/** Final account number, keyed by the row's index in `customers`. */
	numbers: Map<number, string>;
	notes: NumberingNote[];
}

/**
 * Resolve the final account number for every customer row, applying the office
 * numbering convention: per unit, the LIVE owner holds the bare number and every
 * closed/hidden row takes a "-N" suffix — ALWAYS, even when it is the only row for
 * its unit, so a closed account can never hold the number the next owner needs.
 *
 * Rows are grouped by the BASE number, with any "-N" the QB name itself carries
 * stripped. QB sometimes has it backwards — "5977AS - (CLSD) Epcon" beside
 * "5977AS-1 - Wilt" — and grouping on the literal number put both on "5977AS-1".
 * Grouped by base, the one live row takes "5977AS" and the closed row "5977AS-1",
 * and a note says so.
 *
 * Live rows otherwise keep the number their QB name carries; a second live row on the
 * same number takes the next free suffix (noted — two live owners of one unit is
 * something a person should look at). Closed rows keep a "-N" their name already
 * carries when it is free, else take the next free suffix, in file order.
 */
export function disambiguateWithNotes(customers: ReadonlyArray<{ raw: Record<string, string> }>): Disambiguation {
	const numbers = new Map<number, string>();
	const notes: NumberingNote[] = [];
	const groups = new Map<string, Array<{ idx: number; own: string; closed: boolean; name: string }>>();
	customers.forEach((c, idx) => {
		const rawName = (c.raw.NAME || '').trim();
		if (!rawName) return;
		// Same flag-strip → parse order as buildIifCustomerRow, so a leading flag
		// token can't give the two different account numbers for one row.
		const { accountNumber, closed } = parseCustomerName(parseNameFlags(rawName).name);
		if (!accountNumber) return;
		const base = accountNumber.replace(/-\d+$/, '');
		const hidden = (c.raw.HIDDEN || '').toUpperCase() === 'Y' || closed;
		if (!groups.has(base)) groups.set(base, []);
		groups.get(base)!.push({ idx, own: accountNumber, closed: hidden, name: rawName });
	});
	for (const [base, members] of groups.entries()) {
		const taken = new Set<string>();
		const nextFree = (): string => {
			for (let n = 1; ; n++) {
				const candidate = `${base}-${n}`;
				if (!taken.has(candidate)) return candidate;
			}
		};
		const live = members.filter((m) => !m.closed);
		const closedRows = members.filter((m) => m.closed);
		// Office numbering: the unit's only live owner belongs on the bare number.
		const reversed = live.length === 1 && live[0].own !== base;
		for (const m of live) {
			let n = reversed ? base : m.own;
			if (taken.has(n)) n = nextFree();
			taken.add(n);
			numbers.set(m.idx, n);
		}
		// Two live owners of one unit is something a person should look at — whether or not
		// their numbers collided ("100A - Smith" beside "100A-1 - Jones" is just as wrong).
		// One note per unit, so the preflight's count is a count of units (Codex P2).
		if (live.length > 1) {
			notes.push({
				base,
				kind: 'duplicate-live',
				message: `${base}: more than one LIVE QuickBooks customer — `
					+ live.map((m) => `"${m.name}" imports as ${numbers.get(m.idx)}`).join(', '),
			});
		}
		if (reversed) {
			notes.push({
				base,
				kind: 'office-numbering',
				message: `${base}: closed builder and live owner swapped to office numbering — `
					+ `"${live[0].name}" imports as ${base}${closedRows.length ? `, the closed account${closedRows.length > 1 ? 's' : ''} as ${base}-N` : ''}`,
			});
		}
		// A closed row never holds the bare number; keep its own "-N" if free.
		taken.add(base);
		for (const m of closedRows) {
			const n = m.own !== base && !taken.has(m.own) ? m.own : nextFree();
			taken.add(n);
			numbers.set(m.idx, n);
		}
	}
	return { numbers, notes };
}

/** The numbers only — see `disambiguateWithNotes`. */
export const disambiguateAccountNumbers = (customers: ReadonlyArray<{ raw: Record<string, string> }>): Map<number, string> =>
	disambiguateWithNotes(customers).numbers;

/** What /customers/bulk is sent per customer: the built row minus client-only fields. */
export type CustomerImportRow = Omit<IifCustomerRow, 'missingAccountNumber' | 'rawName' | 'addressFromBillTo'>;

export interface CustomerImportRows {
	/** Closed/archived rows FIRST (stable otherwise) — see the comment in the body. */
	rows: CustomerImportRow[];
	/** Raw names of customers with no unit/account-number prefix (not imported). */
	skippedNames: string[];
	notes: NumberingNote[];
}

/** Every IIF customer → the rows the importer POSTs, in POST order. */
export function buildCustomerImportRows(customers: ReadonlyArray<{ raw: Record<string, string> }>): CustomerImportRows {
	const { numbers, notes } = disambiguateWithNotes(customers);
	const rows: CustomerImportRow[] = [];
	const skippedNames: string[] = [];
	customers.forEach((cust, idx) => {
		const built = buildIifCustomerRow({ cust: cust as Parameters<typeof buildIifCustomerRow>[0]['cust'], disambiguatedAccountNumber: numbers.get(idx) });
		if (!built) return;
		// Actually skip it. Sending the row anyway made the server create a person
		// with no account — the source of most orphaned contacts.
		if (built.missingAccountNumber) {
			skippedNames.push(built.rawName);
			return;
		}
		// `closed` / `closedOn` travel too: the server only knew `archivedAt`, so every
		// QB-closed customer was created `status = 'active'` (#635).
		const { missingAccountNumber: _m, rawName: _r, addressFromBillTo: _b, ...row } = built;
		rows.push(row);
	});
	// Closed/archived rows FIRST (stable otherwise). Each moves its account off the bare
	// number onto its suffixed one, so by the time an active row asks for the bare number
	// — in this chunk or a later one — the seller or legacy closed account has vacated it
	// instead of absorbing the new customer.
	rows.sort((a, b) => Number(!a.archivedAt) - Number(!b.archivedAt));
	return { rows, skippedNames, notes };
}
